/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { CSSProperties, ReactNode } from "react";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";
import { VegClassifier } from "@swiggy-private/dls-web-recipe-common-web/dist/components/FoodItem/types";
import type { IApiConfigs } from "@swiggy-private/portal-dineout-context";
import type { IMenuCategoryGrid } from "@swiggy-private/portal-dineout-ui";

export interface IMenuSuccessDataInfo {
    statusMessage?: string;
    cards?: IPLWidgetControllerCards[];
}

export interface IMenuFailureDataInfo {
    errorCode: number;
    errorMessage: string;
}

export interface ICollectionSuccessDataInfo {
    statusMessage?: string;
    cards?: IPLWidgetControllerCards[];
}

export interface ICollectionFailureDataInfo {
    errorCode: number;
    errorMessage: string;
}

export interface IMenuDataInfo {
    configs?: IApiConfigs;
    tid?: string;
    sid?: string;
    deviceId?: string;
    success: IMenuSuccessDataInfo;
    failure: IMenuFailureDataInfo;
    statusCode: 0 | 1;
}

export interface ICollectionDataInfo {
    configs?: IApiConfigs;
    tid?: string;
    sid?: string;
    deviceId?: string;
    success: ICollectionSuccessDataInfo;
    failure: ICollectionFailureDataInfo;
    statusCode: 0 | 1;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export enum WIDGET_TYPES {
    DISH = "type.googleapis.com/swiggy.presentation.food.v2.Dish",
    ITEM_CATEGORY_TYPE = "type.googleapis.com/swiggy.presentation.food.v2.ItemCategory",
    NESTED_ITEM_CATEGORY_TYPE = "type.googleapis.com/swiggy.presentation.food.v2.NestedItemCategory",
    RESTAURANT_INFO = "type.googleapis.com/swiggy.presentation.food.v2.Restaurant",
    MENU_VEG_FILTER_AND_BADGE = "type.googleapis.com/swiggy.presentation.food.v2.MenuVegFilterAndBadge",
    PILLS = "type.googleapis.com/swiggy.gandalf.widgets.v2.Pills",
    MENU_CATEGORY_GRID = "type.googleapis.com/swiggy.presentation.dinersone.v1.MenuCategoryGrid",
    CART_ITEM_LIST = "type.googleapis.com/swiggy.presentation.dinersone.v1.CartInfo",
    ENTITY_INFO_WITH_MASTHEAD = "type.googleapis.com/swiggy.presentation.dinersone.v1.EntityInfoWithMasthead",
    REFINE_WIDGET = "type.googleapis.com/swiggy.gandalf.widgets.v2.InlineViewFilterSortWidget",
    REFINE_WIDGET_CUSTOM = "type.googleapis.com/swiggy.gandalf.widgets.v2.InlineViewFilterSortWidgetCustom",
}

interface IVariantsNew {
    exclude_list: any[];
    variant_groups: IVariantGroupsEntity[];
    excludeList: any[];
    variantGroups: IVariantGroupsEntity[];
}
interface IVariants {
    exclude_list: any[];
    excludeList: any[];
    variantGroups: IVariantGroupsEntity[];
}
interface IAddonCombinationsEntity {
    groupId: string;
    addonId: string;
}
interface IPricingModelsEntity {
    variations?: IVariantGroupsEntity[];
    price: number;
    addonCombinations?: IAddonCombinationsEntity[];
}
interface IVariantsV2 {
    variantGroups?: IVariantGroupsEntity[];
    pricingModels?: IPricingModelsEntity[];
}
interface IVariantGroupsEntity {
    groupId: string;
    name: string;
    variationId?: string;
    variations?: IVariationsEntity[];
}
interface IVariationsEntity {
    name: string;
    price: number;
    default: number;
    id: string;
    inStock: number;
    isVeg: number;
    variantGroupId: string;
    order: number;
}
interface IChoicesEntity {
    id: number;
    name: string;
    price: number;
    inStock: number;
    isVeg: number;
    order: number;
    default: number;
}

export interface IAddonsEntity {
    groupId: number;
    group_id: number;
    group_name: string;
    choiceId: number;
    groupName: string;
    choices: IChoicesEntity[];
    maxAddons: number;
    maxFreeAddons: number;
    minAddons: number;
    order: number;
    name: string;
    price: number;
}

export interface IDishData {
    id: number | string;
    name: string;
    category?: string;
    description: string;
    cloudinaryImageId?: string;
    imageId?: string;
    recommended?: number;
    inStock: number;
    isSpicy?: boolean;
    isVeg: number;
    enabled?: number;
    displayOrder?: number;
    price: number;
    finalPrice?: number;
    basePrice?: number;
    subtotal?: number;
    total: number;
    subtotalTradeDiscount?: number;
    variants?: IVariants;
    variants_new?: IVariantsNew;
    variantsV2?: IVariantsV2;
    itemAttribute?: {
        vegClassifier: VegClassifier;
    };
    isBestseller?: boolean;
    isGuiltfree?: boolean;
    maxAddons?: number;
    maxFreeAddons?: number;
    addons?: IAddonsEntity[];
    itemScore?: number;
    itemDiscount?: number;
    isPopular?: number;
    showMC?: number;
    customizable?: boolean;
    defaultPrice?: number;
    attributes?: {
        spiceLevel: "VERYSPICY" | "MEDIUMSPICY";
    };
    portionSize?: string;
    itemInfoTags?: {
        title: string;
        subtitle: string;
    }[];
    bulletPoints?: {
        title: string;
        collapsible: boolean;
    }[];
    specialCategoryTag?: {
        backgroundColor: string;
        textColor: string;
        text: string;
    }[];
    nextAvailableAtMessage?: string;
    outOfStockMessage?: string;
}

interface ICategoryDish {
    "@type": WIDGET_TYPES.DISH;
    info: IDishData;
    /** Optional render prop for cross-sell content below the dish. Injected in UI layer. */
    renderCrossSellItems?: () => ReactNode;
}

export interface ICategoryItems {
    card: ICategoryDish;
}

export declare enum CategoryType {
    CATEGORY_TYPE_INVALID = "CATEGORY_TYPE_INVALID",
    CATEGORY_TYPE_ITEM = "CATEGORY_TYPE_ITEM",
    CATEGORY_TYPE_RECOMMENDED = "CATEGORY_TYPE_RECOMMENDED",
    CATEGORY_TYPE_SUGGESTED = "CATEGORY_TYPE_SUGGESTED",
}

export interface INestedItemCategoryType {
    "@type": WIDGET_TYPES.NESTED_ITEM_CATEGORY_TYPE;
    title: string;
    type: CategoryType;
    categories: {
        title: string;
        itemCards: ICategoryItems[];
    }[];
    itemCards: undefined;
    image?: string;
    restaurantId?: string;
    visibleItemCount?: number;
    categoryId?: string | number;
}

export interface IITemCategoryType {
    "@type": WIDGET_TYPES.ITEM_CATEGORY_TYPE;
    title: string;
    type: CategoryType;
    itemCards: ICategoryItems[];
    categories: undefined;
    image?: string;
    restaurantId?: string;
    visibleItemCount?: number;
    categoryId?: string | number;
}

export declare type IItemCategoryComponentProps = IITemCategoryType | INestedItemCategoryType;

export enum ParentItemCategoryType {
    FOOD = "Food",
    DRINKS = "Drinks",
}

export interface IPillsProps {
    "@type": WIDGET_TYPES.PILLS;
    tabs?: ITab[];
}

export interface ITab {
    name: string;
    id: string;
    selectedIcon: string;
    unselectedIcon: string;
    selectedTextColor?: keyof IColors;
    unselectedTextColor?: keyof IColors;
    selectedBackgroundColor?: keyof IColors;
    unselectedBackgroundColor?: keyof IColors;
    selectedBorderColor?: keyof IColors;
    unselectedBorderColor?: keyof IColors;
    showRightIcon?: boolean;
}

export declare type ICardsEntity = IItemCategoryComponentProps | IPillsProps | IMenuCategoryGrid;

export interface IPLWidgetControllerCards {
    card: {
        card: ICardsEntity;
        relevance?: {
            type?: string;
            sectionId?: string;
        };
    };
}

export enum REDIRECTIONTYPES {
    MENU = "MENU",
    TRACK = "TRACK",
    BACK = "BACK",
    BILL_SUMMARY = "BILL_SUMMARY",
    COLLECTIONS = "COLLECTIONS",
    PAYMENT = "PAYMENT",
    CART_ORDERS = "CART_ORDERS",
}

export interface IQuickAction {
    actionButton?: {
        id: number;
        label: string;
        name: string;
        icon?: {
            id: string;
        };
        cta?: {
            type: string;
        };
    };
    successIcon?: string;
    seconds?: number;
    style?: CSSProperties;
    alignment?: "center" | "left" | "right";
    cancelButtonStyle?: CSSProperties;
    retryButtonStyle?: CSSProperties;
}

export interface IInstructionAction {
    action: "save" | "remove" | "add";
    specialInstructions?: string;
    message?: string;
}

export interface IPartnerOrderToastCta {
    text: string;
    link: string;
    type: string;
}

export interface IPartnerOrderToast {
    icon: string;
    message: string;
    cta: IPartnerOrderToastCta;
    style?: CSSProperties;
    delayActionDuration?: number;
    progressColor?: keyof IColors;
}

export interface HandleOrderToastParams {
    partnerOrderToast?: IPartnerOrderToast;
    showMenuNudge: boolean;
    isCartItemAvailable: boolean;
    onBillSyncApiReceived: (partnerOrderToast: IPartnerOrderToast, showMenuNudge?: boolean) => void;
}
